import i18n, { type BackendModule, type ResourceKey } from "i18next";
import moment from "moment";
import { initReactI18next } from "react-i18next";

import { BASE_URL } from "@/configs";
import { refreshPageMeta } from "@/lib/pageMeta";

import { LANG_CODES, baseLanguage, defaultLang, matchLanguage } from "./configLang";

const NS = "translations";
// Only an explicit pick (the picker or ?lang=) is stored; everyone else follows the browser.
const CHOICE_KEY = "skystake:lang";
// LanguageDetector used to cache every visit here, "en" included, so only a non-English value was a real pick.
const LEGACY_KEY = "i18nextLng";

// Every language is its own chunk, English too (~280 KB the entry no longer carries); index.tsx
// starts the site check alongside it, so the first render waits no longer than before.
const bundles: Record<string, () => Promise<{ default: ResourceKey }>> = {
  en: () => import("./langs/en.json"),
  es: () => import("./langs/es.json"),
  "es-AR": () => import("./langs/es-AR.json"),
  it: () => import("./langs/it.json"),
  pt: () => import("./langs/pt.json"),
};

// moment has no es-AR; Argentine dates use the Spanish one.
const momentLocaleOf = (lng: string) => baseLanguage(lng);
const momentLocales: Record<string, () => Promise<unknown>> = {
  es: () => import("moment/dist/locale/es"),
  it: () => import("moment/dist/locale/it"),
  pt: () => import("moment/dist/locale/pt"),
};

const storage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode: the pick lasts for this visit only */
    }
  },
  remove: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing stored */
    }
  },
};

const storedChoice = (): string | undefined => {
  const legacy = storage.get(LEGACY_KEY);
  if (legacy !== null) {
    storage.remove(LEGACY_KEY);
    const picked = matchLanguage(legacy);
    if (picked && picked !== defaultLang.value && !storage.get(CHOICE_KEY)) {
      storage.set(CHOICE_KEY, picked);
    }
  }
  return matchLanguage(storage.get(CHOICE_KEY));
};

// ?lang= (ads, affiliates) → stored pick → browser languages in order → English.
const initialLanguage = (): string => {
  const fromQuery = matchLanguage(new URLSearchParams(window.location.search).get("lang"));
  if (fromQuery) {
    storage.set(CHOICE_KEY, fromQuery);
    return fromQuery;
  }
  const stored = storedChoice();
  if (stored) return stored;
  const browser = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of browser) {
    const match = matchLanguage(tag);
    if (match) return match;
  }
  return defaultLang.value;
};

const lazyBundles: BackendModule = {
  type: "backend",
  init: () => {},
  read: (lng, _ns, callback) => {
    const loadBundle = bundles[lng];
    if (!loadBundle) {
      callback(null, {});
      return;
    }
    Promise.all([loadBundle(), momentLocales[momentLocaleOf(lng)]?.()])
      .then(([bundle]) => {
        // Defining a moment locale also makes it the global one; put back the current language's.
        moment.locale(momentLocaleOf(i18n.language || defaultLang.value));
        callback(null, bundle.default);
      })
      .catch((err: Error) => callback(err, false));
  },
};

const overridesLoaded = new Set<string>();

// Text edited in admin (Base > Translations) replaces the bundled strings; the bundle stays if this fails.
// es-AR reads the es overrides: they sit in its fallback chain under its own voseo strings.
const loadOverrides = async (lng: string) => {
  const code = baseLanguage(lng);
  if (overridesLoaded.has(code)) return;
  overridesLoaded.add(code);
  try {
    const res = await fetch(`${BASE_URL}/api/translation?lang=${code}`);
    if (!res.ok) return;
    const overrides = (await res.json()) as Record<string, string>;
    const keys = Object.keys(overrides);
    if (!keys.length) return;
    // Adding to a bundle before it loads would mark it loaded and skip the lazy chunk.
    if (!i18n.hasResourceBundle(code, NS)) await i18n.loadLanguages(code);
    for (const key of keys) i18n.addResource(code, NS, key, overrides[key]);
    // Re-render screens already showing the bundled text.
    if (baseLanguage(i18n.language) === code) void i18n.changeLanguage(i18n.language);
  } catch {
    overridesLoaded.delete(code);
  }
};

// Fires once the language's bundles are in the store, the first language included.
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
  // og:locale follows the language.
  refreshPageMeta();
  moment.locale(momentLocaleOf(lng));
  void loadOverrides(lng);
});

/** Resolves once the first language's strings are loaded; render after it so a Spanish visitor never sees English. */
export const i18nReady: Promise<unknown> = i18n
  .use(lazyBundles)
  .use(initReactI18next)
  .init({
    lng: initialLanguage(),
    // es-AR holds only its voseo overrides, the rest comes from es.
    fallbackLng: { "es-AR": ["es", "en"], default: [defaultLang.value] },
    supportedLngs: LANG_CODES,
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    debug: false,
    ns: [NS],
    defaultNS: NS,
    interpolation: {
      escapeValue: false,
    },
  })
  .catch(() => undefined);

/** The player's explicit pick: stored, then applied once its bundle is loaded. */
export const setLanguage = async (lng: string) => {
  const code = matchLanguage(lng) ?? defaultLang.value;
  storage.set(CHOICE_KEY, code);
  await i18n.changeLanguage(code);
  return code;
};

export default i18n;
